/**
 * Upload cost calculation.
 *
 *
 * Pricing model:
 * - Audio/video: per-minute rate with 15-second minimum billing
 * - Text (PDF/DOCX/TXT): flat per-note cost ($0.01 default)
 */

/** Minimum billable duration in seconds */
const MIN_BILLABLE_SECONDS = 15;

/** Default text note cost */
const DEFAULT_TEXT_NOTE_COST = 0.01;

/**
 * Calculate cost for an audio/video file.
 *
 * @param durationSeconds - Duration of the file in seconds
 * @param perMinuteRate - Cost per minute from the subscription plan (insightCost)
 * @returns Cost in dollars, rounded to 2 decimal places
 */
export function calculateMediaCost(
  durationSeconds: number,
  perMinuteRate: number
): number {
  const billableDuration = Math.max(durationSeconds, MIN_BILLABLE_SECONDS);
  const cost = perMinuteRate * (billableDuration / 60);
  return parseFloat(cost.toFixed(2));
}

/**
 * Calculate cost for a text file (PDF, DOCX, TXT).
 *
 * @param costPerNote - Cost per text note (defaults to $0.01)
 * @param count - Number of text files
 * @returns Cost in dollars, rounded to 2 decimal places
 */
export function calculateTextCost(
  costPerNote: number = DEFAULT_TEXT_NOTE_COST,
  count: number = 1
): number {
  return parseFloat((costPerNote * count).toFixed(2));
}

/** Translation rate: $30 per 1M characters */
const TRANSLATION_COST_PER_MILLION_CHARS = 30;

/** Char estimation rate for audio/video: 800 chars per minute */
const CHARS_PER_MINUTE_ESTIMATE = 800;

/** Minimum translation cost in dollars */
const MIN_TRANSLATION_COST = 0.01;

/** Dubbing rate: $1 per minute (Angular translate.component.ts:1271-1275) */
export const DUBBING_COST_PER_MINUTE = 1;

/**
 * Calculate translation cost for an audio/video file.
 *
 * @param durationSeconds - Duration in seconds
 * @returns { cost, characters } — cost in dollars (2dp, $0.01 min), estimated chars
 *
 * Formula: chars = (duration / 60) × 800; cost = max((chars / 1M) × $30, $0.01).
 * Matches Angular translate.component.ts:480-490.
 */
export function calculateTranslationCost(durationSeconds: number): {
  cost: number;
  characters: number;
} {
  const minutes = durationSeconds / 60;
  const characters = Math.round(minutes * CHARS_PER_MINUTE_ESTIMATE);
  const raw = (characters / 1_000_000) * TRANSLATION_COST_PER_MILLION_CHARS;
  const cost = parseFloat(Math.max(raw, MIN_TRANSLATION_COST).toFixed(2));
  return { cost, characters };
}

/**
 * Translation cost for a single text file given an extracted character count.
 *
 * Mirrors Angular translate.component.ts L487-489: characters / 1M × $30,
 * floored at MIN_TRANSLATION_COST ($0.01).
 *
 * @param characters - Extracted character count
 * @returns Translation cost in dollars (2dp, $0.01 minimum)
 */
export function calculateTextTranslationCost(characters: number): number {
  if (characters <= 0) return MIN_TRANSLATION_COST;
  const raw = (characters / 1_000_000) * TRANSLATION_COST_PER_MILLION_CHARS;
  return parseFloat(Math.max(raw, MIN_TRANSLATION_COST).toFixed(2));
}

/**
 * Per-file dubbing cost. $1/min × duration. Matches Angular translate.component.ts:1271-1275.
 */
export function calculateDubbingCost(durationSeconds: number): number {
  if (durationSeconds <= 0) return 0;
  return parseFloat(((durationSeconds / 60) * DUBBING_COST_PER_MINUTE).toFixed(2));
}

/**
 * Aggregate batch translation cost AFTER quota offset.
 *
 * @param totalGrossChars - Sum of per-file gross characters across files
 * @param freeCharacters - User's remaining free quota (from useUploadPricing)
 * @returns Aggregate translation cost in dollars; 0 when fully covered, $0.01 min when billable > 0.
 *
 * Mirrors Angular `enableUploadBtn` aggregate calc (translate.component.ts:619-665).
 * Per-file `translationCost` stays GROSS — server reconciles via totalAmount transaction layer.
 */
export function calculateBatchTranslationCost(
  totalGrossChars: number,
  freeCharacters: number
): number {
  const billableChars = Math.max(totalGrossChars - freeCharacters, 0);
  if (billableChars === 0) return 0;
  const raw = (billableChars / 1_000_000) * TRANSLATION_COST_PER_MILLION_CHARS;
  return parseFloat(Math.max(raw, MIN_TRANSLATION_COST).toFixed(2));
}

